import React, { type JSX, RefAttributes } from 'react'

import {
  Accordion,
  Column,
  HoverUnderline,
  LocaleButton,
  LocaleButtonIcon,
  LocaleButtonText,
  Media,
  Spacer,
} from '@syconium/little-miss-figgy'

import { PrimaryNavLocalizationDialogContent } from '../../../../app/_components/chrome/PrimaryNavLocalization.client'
import {
  NextDialog,
  NextDialogProvider,
  NextDialogTrigger,
} from '../../../../app/_components/dialogs/NextDialog.client'
import { useTranslatedLocalization } from '../../../../app/_hooks/useTranslatedLocalization'
import { useFixturesContext } from '../../../../brunswick/containers/fixtures'

import {
  ContactLink,
  CountryWrapper,
  FooterCopyright,
  FooterDarkText,
  FooterLeft,
  FooterLinksDivider,
  FooterLinksList,
  FooterLinksListItem,
  FooterListLink,
  FooterSocialLinkList,
  MenuLink,
  MenuLinkItem,
  MenuList,
  MenuListItem,
  MenuTitle,
  Promo,
  PromoHashtag,
  PromoSocialItem,
  PromoSocialLink,
  PromoSocialLinkSRText,
  PromoTagline,
  StyledFooter,
} from './styles'

const showChat = true

export interface FooterSocialLinkProps {
  href: string
  text: string
  icon: React.ReactNode
}

export const FooterSocialLink: React.FC<FooterSocialLinkProps> = ({ href, text, icon }) => {
  const pageName = text.length ? text.charAt(0).toUpperCase() + text.slice(1) : ''
  const ariaLabel = `Go to Wearfigs ${pageName} Page`

  return (
    <PromoSocialItem role='listitem'>
      <PromoSocialLink aria-label={ariaLabel} href={href}>
        {icon && (
          <span aria-hidden='true' role='presentation'>
            {icon}
          </span>
        )}
        <PromoSocialLinkSRText>{text}</PromoSocialLinkSRText>
      </PromoSocialLink>
    </PromoSocialItem>
  )
}

export interface FooterPromoProps {
  children?: React.ReactNode
  hashtag: string
  socialLinks: FooterSocialLinkProps[]
}

export const FooterPromo: React.FC<FooterPromoProps> = ({ children, hashtag, socialLinks }) => {
  return (
    <Promo>
      <PromoTagline>{children}</PromoTagline>
      <PromoHashtag>#{hashtag}</PromoHashtag>
      {socialLinks.length > 0 && (
        <FooterSocialLinkList role='list'>
          {socialLinks.map((link, index) => (
            <FooterSocialLink {...link} key={`${link.text}-${index}`} />
          ))}
        </FooterSocialLinkList>
      )}
    </Promo>
  )
}

export interface LinkItem {
  href: string
  title: string
}

export interface FooterGetHelpProps {
  helpCenter: LinkItem
  shipping: LinkItem
  returnsAndExchanges: LinkItem
  teamsOrders: LinkItem
  contactUs: LinkItem
  phone: {
    number: string
    cta: string
    timeText: string
    availableDaysText: string
  }
  chat: {
    title: string
    href: string
    timeText: string
    availableDaysText: string
  }
  children?: React.ReactNode
}

export const FooterGetHelp: React.FC<FooterGetHelpProps> = ({
  helpCenter,
  shipping,
  returnsAndExchanges,
  teamsOrders,
  contactUs,
  phone,
  children,
  chat,
}) => {
  return (
    <>
      <FooterMenuTitle>Get help</FooterMenuTitle>
      <FooterMenuList>
        <FooterMenuListItem>
          <FooterMenuLink href={helpCenter.href}>{helpCenter.title}</FooterMenuLink>
        </FooterMenuListItem>
        <FooterMenuListItem>
          <FooterMenuLink href={shipping.href}>{shipping.title}</FooterMenuLink>
        </FooterMenuListItem>
        <FooterMenuListItem>
          <FooterMenuLink href={returnsAndExchanges.href}>
            {returnsAndExchanges.title}
          </FooterMenuLink>
        </FooterMenuListItem>
        <FooterMenuListItem>
          <FooterMenuLink href={teamsOrders.href}>{teamsOrders.title}</FooterMenuLink>
        </FooterMenuListItem>
        <FooterMenuListItem>
          <FooterMenuLink href={contactUs.href}>{contactUs.title}</FooterMenuLink>
        </FooterMenuListItem>
        {phone && (
          <FooterMenuListItem>
            <ContactLink href={`tel:${phone.number}`}>{phone.number}</ContactLink>
            <br />
            <FooterDarkText>{phone.cta}</FooterDarkText>
            <br />
            <FooterDarkText>{phone.timeText}</FooterDarkText>
            <br />
            <FooterDarkText>{phone.availableDaysText}</FooterDarkText>
          </FooterMenuListItem>
        )}
        {showChat && (
          <FooterMenuListItem>
            <div>{chat.title}</div>
            <FooterDarkText>{chat.timeText}</FooterDarkText>
            <br />
            <FooterDarkText>{chat.availableDaysText}</FooterDarkText>
          </FooterMenuListItem>
        )}
        {children}
      </FooterMenuList>
    </>
  )
}

export interface FooterReferAFriendProps {
  href: string
  text: string
}

export const FooterReferAFriend: React.FC<FooterReferAFriendProps> = ({ href, text }) => {
  return (
    <>
      <Media greaterThanOrEqual='md'>
        <Spacer height={24} />
        {/* TODO: Handle this auto disabled ESLint rule. */}
        {/* eslint-disable-next-line rulesdir/require-routing-service */}
        <a href={href}>
          <MenuTitle>{text}</MenuTitle>
        </a>
      </Media>
      <Media lessThan='md'>
        <FooterLink href={href}>{text}</FooterLink>
      </Media>
    </>
  )
}

export const FooterLink: React.FC<React.AnchorHTMLAttributes<HTMLAnchorElement>> = ({
  children,
  ...anchorProps
}) => (
  <MenuLinkItem {...anchorProps}>
    <MenuTitle>{children}</MenuTitle>
  </MenuLinkItem>
)

export interface FooterMenuProps {
  children?: React.ReactNode
  accordionIndex: number
  isAccordionDisabled?: boolean
}

export const FooterMenu: React.FC<FooterMenuProps> = ({
  children,
  accordionIndex,
  isAccordionDisabled,
}) => {
  return (
    <>
      <Media greaterThanOrEqual='md'>
        <Column desktop={2}>{children}</Column>
      </Media>
      <Media lessThan='md'>
        <Accordion.Section id={accordionIndex} disabled={isAccordionDisabled}>
          {children}
        </Accordion.Section>
      </Media>
    </>
  )
}

export const FooterMenuTitle: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  return (
    <>
      <Media greaterThanOrEqual='md'>
        <MenuTitle>{children}</MenuTitle>
      </Media>
      <Media lessThan='md'>
        <Accordion.Header>
          <MenuTitle>{children}</MenuTitle>
        </Accordion.Header>
      </Media>
    </>
  )
}

export const FooterMenuList: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  return (
    <>
      <Media greaterThanOrEqual='md'>
        <MenuList>{children}</MenuList>
      </Media>
      <Media lessThan='md'>
        <Accordion.Body>
          <MenuList>{children}</MenuList>
        </Accordion.Body>
      </Media>
    </>
  )
}

export const FooterMenuListItem: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  return <MenuListItem>{children}</MenuListItem>
}

export interface FooterMenuLinkProps {
  children: React.ReactNode
  href: string
}

export const FooterMenuLink: React.FC<FooterMenuLinkProps> = ({ children, href }) => {
  return (
    <HoverUnderline>
      <MenuLink href={href}>{children}</MenuLink>
    </HoverUnderline>
  )
}

export interface FooterFooterProps {
  asTag?: keyof JSX.IntrinsicElements
  copyright: string
  legalLinks?: LinkItem[]
  children?: React.ReactNode
}

export const FooterFooter: React.FC<FooterFooterProps> = ({
  asTag = 'footer',
  copyright,
  legalLinks,
}) => {
  const fixtures = useFixturesContext()
  const translatedLocalization = useTranslatedLocalization(fixtures)
  const regionName = translatedLocalization.data.region.name
  const languageName = translatedLocalization.data.languageName
  const flagSrc = translatedLocalization.data.region.flagSrc
  const languageCount = translatedLocalization.data.region.languagesArray.length
  const languagePostfix = languageCount > 1 ? ` | ${languageName}` : ''

  return (
    <StyledFooter as={asTag}>
      <FooterLeft>
        <FooterCopyright>{copyright}</FooterCopyright>
        <FooterLinksList role='list' divider={<FooterLinksDivider>•</FooterLinksDivider>}>
          {legalLinks?.map(link => {
            return (
              <FooterLinksListItem key={link.title} role='listitem'>
                <FooterListLink href={link.href}> {link.title} </FooterListLink>
              </FooterLinksListItem>
            )
          })}
        </FooterLinksList>
      </FooterLeft>
      <NextDialogProvider>
        <CountryWrapper>
          <NextDialogTrigger render={<LocaleButton isColorInverted={true} />}>
            <LocaleButtonIcon alt='' src={flagSrc} />
            <LocaleButtonText>{`${regionName}${languagePostfix}`}</LocaleButtonText>
          </NextDialogTrigger>
        </CountryWrapper>
        <NextDialog unmountOnHide>
          <PrimaryNavLocalizationDialogContent variant='full' />
        </NextDialog>
      </NextDialogProvider>
    </StyledFooter>
  )
}

export default interface FooterProps extends RefAttributes<HTMLDivElement> {
  children?: React.ReactNode
  promoChildren(): React.ReactNode
  hashtag: string
  copyright: string
  termsLinkHref: string
  termsLinkText: string
  privacyLinkHref: string
  privacyLinkText: string
}
